import { CDI_DEFAULT_META_LABELS } from '@utils/clienteBenchmark'
import { normalizeGroupAvisos } from '@modules/consolidator/utils/adapt-group-wallet-analysis-response/adapt-group-wallet-analysis-response'

const STATUS_MESSAGES = {
  200: 'OK',
  400: 'Não foi possível carregar a carteira. Verifique os filtros.',
  '400_MAX_ASSETS':
    'Muitos ativos. Refine os filtros para reduzir o resultado.',
  403: 'Você não tem permissão para acessar este cliente.',
  404: 'Cliente sem ativos cotizados.',
  500: 'Erro inesperado. Tente novamente.',
}

export const adaptWalletAnalysisResponse = (response) => {
  if (!response) {
    return buildErrorAdapter({ status: 500, message: STATUS_MESSAGES[500] })
  }
  const status = resolveStatus(response)
  const builder = STATUS_BUILDERS[status] || STATUS_BUILDERS[500]
  return builder(response)
}

export const coerceWalletAnalysisPayload = (payload) =>
  payload && typeof payload === 'object' ? payload : null

export const BACKEND_NO_PORTFOLIO_KEY = 'Sem classificação'

const resolveStatus = (response) => {
  const inner = response.value && response.value.status
  if (typeof inner === 'number') return inner
  return typeof response.status === 'number' ? response.status : 500
}

const resolveMessage = (response, fallback) => {
  const backend =
    (response && response.value && response.value.message) ||
    (response && response.message)
  return backend || fallback
}

const buildErrorAdapter = ({ status, message }) => ({
  ok: false,
  status,
  message,
  warnings: [],
  currency: null,
  portfolio_statistics: null,
  monthly_return: null,
  graph_composition: null,
  portfolio_series: null,
  breakdown: null,
  evolucao_patrimonial: null,
  liquidez: null,
  monthly_return_table: null,
  filters: null,
})

const buildBadRequestAdapter = (response) => {
  const isMaxAssets = Boolean(response.value && response.value.max_assets)
  const fallback = isMaxAssets
    ? STATUS_MESSAGES['400_MAX_ASSETS']
    : STATUS_MESSAGES[400]
  return buildErrorAdapter({
    status: 400,
    message: resolveMessage(response, fallback),
  })
}

const buildNoCotizedAssetsAdapter = (response) => ({
  ...buildErrorAdapter({
    status: 404,
    message: resolveMessage(response, STATUS_MESSAGES[404]),
  }),
  warnings: extractWarnings(response),
})

const buildSuccessAdapter = (response) => {
  const value = response.value || {}
  const portfolio = value.portfolio || {}
  const portfolioData = portfolio.data || {}
  const warnings = extractWarnings(response)

  return {
    ok: true,
    status: 200,
    message: resolveMessage(response, STATUS_MESSAGES[200]),
    warnings,
    currency: portfolio.currency || null,
    portfolio_statistics: withBenchmarksFallback(
      portfolioData.statistics,
      value.breakdown
    ),
    monthly_return: adaptMonthlyReturn(portfolioData),
    graph_composition: adaptGraphComposition(portfolioData.graph_composition),
    portfolio_series: adaptPortfolioSeries(portfolioData),
    breakdown: adaptBreakdown(value.breakdown || {}),
    evolucao_patrimonial: adaptEvolucaoPatrimonial(value.evolucao_patrimonial),
    cliente_benchmark_meta: resolveClienteBenchmarkMeta(value, portfolioData),
    ...buildRawSlots(value, portfolioData),
  }
}

const withBenchmarksFallback = (statistics, breakdownRaw) => {
  if (!statistics) return null
  if (statistics.benchmarks_statistics) return statistics
  const fromBreakdown = pickBenchmarksFromBreakdown(breakdownRaw)
  if (!fromBreakdown) return statistics
  return { ...statistics, benchmarks_statistics: fromBreakdown }
}

const pickBenchmarksFromBreakdown = (breakdownRaw) => {
  const data = breakdownRaw && breakdownRaw.data
  if (!data || typeof data !== 'object') return null
  for (const group of Object.values(data)) {
    const candidate =
      group && group.statistics && group.statistics.benchmarks_statistics
    if (candidate && typeof candidate === 'object') return candidate
  }
  return null
}

const resolveClienteBenchmarkMeta = (value, portfolioData) => {
  const meta = value && value.cliente_benchmark_meta
  if (!meta) return null
  if (CDI_DEFAULT_META_LABELS.includes(meta.label)) return null
  if (meta.key && !isClienteBenchmarkSeriesPresent(meta.key, portfolioData)) {
    return null
  }
  return meta
}

const isClienteBenchmarkSeriesPresent = (key, portfolioData) => {
  const benchmarks = portfolioData && portfolioData.benchmarks
  return (
    benchmarks &&
    typeof benchmarks === 'object' &&
    Array.isArray(benchmarks[key])
  )
}

const buildGroupSlots = (value) => ({
  grupo: value.grupo || null,
  frescor_por_membro: value.frescor_por_membro || [],
  avisos: value.avisos ? normalizeGroupAvisos(value.avisos) : null,
  membros: Array.isArray(value.membros) ? value.membros : [],
})

const buildRawSlots = (value, portfolioData) => {
  const suitability = value.suitability || portfolioData.suitability || null
  return {
    posicao_completa: value.posicao_completa || null,
    posicao_expandida: value.posicao_expandida || null,
    suitability,
    composicao_fundos:
      (suitability && suitability.composicao_fundos) ||
      portfolioData.composicao_fundos ||
      null,
    liquidez: value.liquidez || null,
    monthly_return_table: portfolioData.monthly_return_table || null,
    filters: value.filters || null,
    data_atualizacao_dados: value.data_atualizacao_dados || null,
    data_cotizacao: value.data_cotizacao || null,
    ...buildGroupSlots(value),
  }
}

const STATUS_BUILDERS = {
  200: buildSuccessAdapter,
  400: buildBadRequestAdapter,
  403: (response) =>
    buildErrorAdapter({
      status: 403,
      message: resolveMessage(response, STATUS_MESSAGES[403]),
    }),
  404: buildNoCotizedAssetsAdapter,
  500: (response) =>
    buildErrorAdapter({
      status: 500,
      message: resolveMessage(response, STATUS_MESSAGES[500]),
    }),
}

const extractWarnings = (response) => {
  const warnings = response && response.value && response.value.warnings
  return Array.isArray(warnings) ? warnings : []
}

const adaptMonthlyReturn = (portfolioData) => {
  const index = portfolioData.monthly_index
  const values = portfolioData.monthly_return
  if (!Array.isArray(index) || !Array.isArray(values)) return null
  return { index, values }
}

const adaptGraphComposition = (graphComposition) => {
  if (!graphComposition || !graphComposition.groups) return null
  const posicoes = {}
  const fracoes = {}
  for (const [name, entry] of Object.entries(graphComposition.groups)) {
    posicoes[name] = { posicao: entry.pl }
    fracoes[name] = { fracao: entry.weight }
  }
  return {
    data_referencia: graphComposition.data_referencia || null,
    total: graphComposition.total || 0,
    posicoes,
    fracoes,
  }
}

const adaptPortfolioSeries = (portfolioData) => {
  if (!Array.isArray(portfolioData.index) || portfolioData.index.length === 0) {
    return null
  }
  return {
    index: portfolioData.index,
    pl: portfolioData.PL || [],
    rentabilidade: portfolioData.rentabilidade || [],
    rendimento: portfolioData.rendimento || [],
    pl0_ref: portfolioData.pl0_ref || [],
    benchmarks: portfolioData.benchmarks || {},
  }
}

const adaptBreakdown = (breakdown) => {
  if (!breakdown || !breakdown.data) return null
  return {
    by: breakdown.by || null,
    data: breakdown.data,
  }
}

const adaptEvolucaoPatrimonial = (raw) => {
  if (!raw) return null
  const data = Array.isArray(raw.data) ? raw.data : []
  const patrimonio = Array.isArray(raw.patrimonio) ? raw.patrimonio : []
  if (data.length === 0 || patrimonio.length === 0) return null
  return {
    data,
    patrimonio,
    rentabilidades: raw.rentabilidades || null,
    data_referencia: raw.data_referencia || null,
  }
}

export const buildLegacyStatsShape = (adapted) => {
  if (!adapted) return {}

  const groups = extractBreakdownGroups(adapted)
  const result = {}

  for (const [name, group] of Object.entries(groups)) {
    result[name] = buildGroupStats(group)
  }

  if (adapted.portfolio_statistics) {
    result['Todos'] = sanitizeStats(adapted.portfolio_statistics)
  }

  return result
}

const extractBreakdownGroups = (adapted) => {
  if (!adapted.breakdown) return {}
  if (!adapted.breakdown.data) return {}
  return adapted.breakdown.data
}

const buildGroupStats = (group) => {
  if (!group) return {}
  if (!group.statistics) return {}
  return sanitizeStats(group.statistics)
}

const sanitizeStats = (stats) => {
  const result = { ...stats }
  if (typeof result.pl === 'number' && Number.isNaN(result.pl)) {
    result.pl = 0
  }
  if (!result.benchmarks_statistics) {
    result.benchmarks_statistics = {}
  }
  return result
}
